const Order = require('@/lib/Orders/Order');
const OrderSearchResults = require('@/lib/Orders/OrderSearchResults');
const { apiResponseToError } = require('@/lib/Api');
const { fromYMD, toYMD } = require('@/functions/date');

class OrderSearcher {
    /**
     * @param {ApiClient} apiClient
     * @param {ApiRequestParams} apiRequestParams
     * @param {Hydrator} hydrator
     * @param {ItemInstanceOrderMapRepository} itemInstanceOrderMapRepository
     * @param {OrderRepository} orderRepository
     * @param orderResource
     * @param {RefValidator} refValidator
     * @param {UserService} userService
     */
    constructor(
        apiClient,
        apiRequestParams,
        hydrator,
        itemInstanceOrderMapRepository,
        orderRepository,
        orderResource,
        refValidator,
        userService
    ) {
        this.apiClient = apiClient;
        this.apiRequestParams = apiRequestParams;
        this.hydrator = hydrator;
        this.itemInstanceOrderMapRepository = itemInstanceOrderMapRepository;
        this.orderRepository = orderRepository;
        this.orderResource = orderResource;
        this.refValidator = refValidator;
        this.userService = userService;

        this.searchedBarcode = null;
    }

    /**
     * @param {OrderSearchCriteria|{customerAccountID?: number}} searchCriteria
     *
     * @return {Promise<OrderSearchResults>}
     */
    async search(searchCriteria) {
        console.log('OrderSearcher.search', searchCriteria);
        this.searchedBarcode = searchCriteria.search;

        try {
            return await this.remoteSearch(searchCriteria);
        } catch (e) {
            console.log('Remote order search failed.', e);
            // The online search failed. Fall back to an offline search below.
        }

        try {
            const order = await this.localGet(searchCriteria.search);

            // Found a matching order in the local cache.
            const results = new OrderSearchResults(false);
            results.appendOrders([order]);
            return results;
        } catch (e) {
            // Didn't find a matching order.
        }

        return new OrderSearchResults(false);
    }

    /**
     * @param {object} requestParams
     * @param {string} startYMD
     * @param {string} startPropName
     * @param {string} endYMD
     * @param {string} endPropName
     */
    addDatesToRequestParams(requestParams, startYMD, startPropName, endYMD, endPropName) {
        const startDate = startYMD ? fromYMD(startYMD) : null;
        let endDate = endYMD ? fromYMD(endYMD) : null;
        if (startDate) {
            requestParams[startPropName] = toYMD(startDate);

            // If the end date hasn't been specified it should be the same as the start date.
            if (!endDate) {
                endDate = startDate;
            }
        }

        if (endDate) {
            requestParams[endPropName] = toYMD(endDate);
            // Format the end date
            requestParams[endPropName] += ' 23:59:59';
        }
    }

    /**
     * @param {OrderSearchCriteria} searchParams
     *
     * @return {{}}
     */
    getRequestParamsForRemoteSearch(searchParams) {
        let requestParams = {};

        if (searchParams.search) {
            requestParams.q = searchParams.search;
        }

        if (searchParams.notes) {
            requestParams.notes = searchParams.notes;
            // Note: we used to set requestParams.orderBy = 'orderID' because this sped up search performance. This is
            // no longer the case however and due to changes in data volume and indexes seems to slow down search speed
            // dramatically. See PROP-1866

            // Cancelled orders were originally included to improve search performance but it no longer seems to be a
            // factor and is now included because that is what clients have come to expect.
            // The UI reflects this in the OrderSearchForm and sets includeCancelled in the OrderSearchCriteria,
            // but we'll force it here to ensure it's always set.
            requestParams.cancelled = 1;
        }

        if (searchParams.customerAccountID) {
            requestParams.customerAccountID = searchParams.customerAccountID;
        }

        this.addDatesToRequestParams(
            requestParams,
            searchParams.orderDateStart,
            'dateStart',
            searchParams.orderDateEnd,
            'dateEnd'
        );

        this.addDatesToRequestParams(
            requestParams,
            searchParams.sessionDateStart,
            'sessionTimeStart',
            searchParams.sessionDateEnd,
            'sessionTimeEnd'
        );

        if (searchParams.sessionWithEvent) {
            requestParams.sessionIDs = searchParams.sessionWithEvent.session.ID;
        }

        if (searchParams.includeCancelled) {
            // Include cancelled orders in the search results.
            requestParams.cancelled = 1;
        }

        if (searchParams.includeUnconfirmed) {
            // Include unconfirmed orders in the search results.
            requestParams.unconfirmed = 1;
        }

        if (searchParams.groupBookingsOnly) {
            requestParams.groupBookingsOnly = 1;
        }

        return requestParams;
    }

    /**
     * @private
     *
     * @param {OrderSearchCriteria} searchCriteria
     *
     * @return {Promise<OrderSearchResults>}
     */
    remoteSearch(searchCriteria) {
        const searchQueryParams = this.getRequestParamsForRemoteSearch(searchCriteria);
        const wasCustomSearch = Object.keys(searchQueryParams).length > 0;
        const ordersPerApiPage = wasCustomSearch ? 100 : 50;

        const queryParams = {
            limit: ordersPerApiPage,
            branchID: this.apiRequestParams.getAllUserBranchIDs().join(','),
            resolve: this.apiRequestParams.getResolveForOrderSearchRequest().join(','),
            ...searchQueryParams,
        };

        const results = new OrderSearchResults(true);
        results.ordersPerApiPage = ordersPerApiPage;
        results.requestIsPending = true;
        results.setSearchQueryParams(searchQueryParams);

        return this.apiClient
            .request('GET', 'orders', queryParams)
            .then((res) => {
                results.appendApiResponse(res, this.hydrator);

                if (results.wasCustomSearch) {
                    // TODO: Currently the API doesn't tell us the total number of pages or results for a search.
                    // This is being worked on in BC-783.
                    // We definitely do not want to always fetch every "next" link header it sends us because then
                    // we would download EVERY order if you did the "recent orders" search.
                    // If we had the number of results we could load the next page on demand when the user presses
                    // the next page button (e.g. to go to page 11). But without the number of results we don't know if
                    // we need to show the next page button or not.
                    //
                    // For that reason, this is currently limited in scope to only loading the next page if the
                    // search is for a specific session. We know that should be a reasonably sensible total number
                    // of orders and we shouldn't end up downloading pages forever.
                    // When the total number of items is available in the API response replace this with logic that
                    // loads the pages on demand as the user navigates through the results.
                    if (searchQueryParams.sessionID) {
                        // Start loading the next page of results in the background.
                        this.loadNextPageOfOrdersIntoSearchResults(results)
                            .catch((e) => {
                                console.error(e);
                            })
                            .finally(() => {
                                results.requestIsPending = false;
                            });
                    }
                }

                return results;
            })
            .catch((e) => {
                results.requestIsPending = false;
                throw e;
            });
    }

    /**
     * @param {OrderSearchResults} results
     *
     * @return {Promise<OrderSearchResults>}
     */
    async loadNextPageOfOrdersIntoSearchResults(results) {
        if (!results.nextApiPageUrl) {
            return results;
        }

        if (results.apiResponsesAppended >= 20) {
            // Prevent runaway requests, where 20 pages of results have already been loaded.
            // That's 20,000 orders, which seems unreasonably large.
            return results;
        }

        results.requestIsPending = true;
        try {
            const response = await this.apiClient.requestUrl('GET', results.nextApiPageUrl);

            results.appendApiResponse(response, this.hydrator);

            // If there is still another page of results, load them.
            if (results.nextApiPageUrl) {
                return this.loadNextPageOfOrdersIntoSearchResults(results);
            }

            return results;
        } catch (e) {
            results.requestIsPending = false;
            throw e;
        }
    }

    /**
     * Search the API for a specific order either by ID or booking ref.
     *
     * @param {number|string} orderIDOrBookingRef
     *
     * @return {Promise<Order>}
     */
    async remoteGet(orderIDOrBookingRef) {
        const order = await this._remoteGet(orderIDOrBookingRef);

        if (order.isConfirmed()) {
            return order;
        }

        // If the order is not confirmed, fetch it again with its actual items.
        return this._remoteGet(orderIDOrBookingRef, { actualOrderItems: 1 });
    }

    /**
     * @private
     *
     * @param {number|string} orderIDOrBookingRef
     * @param {object} [extraParams] Extra query string parameters to send with the request.
     *
     * @return {Promise<Order>}
     */
    async _remoteGet(orderIDOrBookingRef, extraParams = {}) {
        return new Promise((resolve, reject) => {
            this.orderResource
                .get(
                    {
                        id: orderIDOrBookingRef,
                        ...extraParams,
                    },
                    (result) => {
                        const order = this.hydrator.hydrate(result, new Order());
                        resolve(order);
                    },
                    (err) => reject(apiResponseToError(err))
                );
        });
    }

    /**
     * Search our local cache for a specific order either by ID, booking ref, or an item instance ref.
     *
     * @param {number|string} idOrBookingRef Order ID or Booking Reference
     *
     * @return {Promise<Order>}
     */
    async localGet(idOrBookingRef) {
        // If idOrBookingRef looks like an itemInstanceRef see if there is a match in the itemInstanceOrderMap idbtable.
        if (this.refValidator.matchesPattern(idOrBookingRef, this.refValidator.referencePatterns.itemInstance.system)) {
            console.debug('idOrBookingRef matches itemInstance pattern. Searching local cache.');
            let itemInstanceOrderMap = await this.itemInstanceOrderMapRepository.findByItemInstanceRef(idOrBookingRef);
            if (itemInstanceOrderMap) {
                console.debug('Found matching ItemInstanceOrderMap', itemInstanceOrderMap);
                idOrBookingRef = itemInstanceOrderMap.orderID;
            }
        }

        // The promises returned by the orderRepository are actually Dexie promises, not native promises.
        // These must be awaited instead of a then/catch chain, because if we re-throw an error in the catch block
        // of a Dexie promise it sees it as an unhandled rejection, and that get unnecessarily reported to Sentry.
        // TODO: Should we refactor the repositories to return native promises instead of Dexie promises?

        // Try to find by ID first.
        try {
            const order = await this.orderRepository.find(idOrBookingRef);
            order.calculate();
            console.debug('Found order in local cache by ID.');
            return order;
        } catch (e) {
            console.debug("Couldn't find order in local cache by ID.", e);
        }

        // Try to find by bookingRef.
        try {
            const order = await this.orderRepository.findByBookingRef(idOrBookingRef);
            order.calculate();
            console.debug('Found order in local cache by bookingRef.');
            return order;
        } catch (e) {
            console.debug("Couldn't find order in local cache by bookingRef.", e);
        }

        throw new Error('Failed to find order in local cache by ID or bookingRef.');
    }

    getSearchedBarcode() {
        return this.searchedBarcode;
    }

    clearSearchedBarcode() {
        this.searchedBarcode = null;
    }
}

module.exports = OrderSearcher;
